import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Empty,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Tooltip,
    InputNumber,
    Badge,
    Alert,
    Typography
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    ApartmentOutlined,
    GlobalOutlined,
    CopyOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CdnGroups = () => {
    const [groups, setGroups] = useState([]);
    const [availableZones, setAvailableZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const selectedZoneId = Form.useWatch('dns_zone_id', form);

    // 订阅 -> 组 单独授权管理
    const [grantsModalVisible, setGrantsModalVisible] = useState(false);
    const [grantsGroup, setGrantsGroup] = useState(null);
    const [grants, setGrants] = useState([]);
    const [grantsLoading, setGrantsLoading] = useState(false);
    const [newSubscriptionId, setNewSubscriptionId] = useState('');
    const [grantSubmitting, setGrantSubmitting] = useState(false);

    useEffect(() => {
        loadGroups();
        loadAvailableZones();
    }, []);

    const loadGroups = async () => {
        setLoading(true);
        try {
            const response = await cdnService.getCdnGroups();
            if (response.success) {
                setGroups(response.data || []);
            } else {
                message.error(response.error || '加载CDN分组失败');
            }
        } catch (error) {
            message.error('加载CDN分组失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableZones = async () => {
        try {
            const response = await cdnService.getAvailableCdnZones();
            if (response.success) {
                setAvailableZones(response.data || []);
            }
        } catch (error) {
            console.error('加载DNS Zones失败:', error);
        }
    };

    const handleAdd = () => {
        setEditingGroup(null);
        form.resetFields();
        form.setFieldsValue({
            priority: 100,
            status: 'active',
            i18n: {}
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingGroup(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            dns_zone_id: record.dns_zone_id,
            priority: record.priority,
            status: record.status,
            domain_cap: record.domain_cap || null,
            i18n: parseI18n(record.i18n)
        });
        setModalVisible(true);
    };

    const handleDelete = (id, name) => {
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除CDN分组 "${name}" 吗？这将影响使用该分组的所有域名。`,
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await cdnService.deleteCdnGroup(id);
                    if (response.success) {
                        message.success('删除成功');
                        loadGroups();
                    } else {
                        message.error(response.error || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败: ' + error.message);
                }
            }
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            values.i18n = cleanI18n(mergeI18n(editingGroup?.i18n, values.i18n));
            values.domain_cap = values.domain_cap || 0;

            const response = editingGroup
                ? await cdnService.updateCdnGroup({ ...values, id: editingGroup.id })
                : await cdnService.createCdnGroup(values);

            if (response.success) {
                message.success(editingGroup ? '更新成功' : '创建成功');
                setModalVisible(false);
                loadGroups();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请填写所有必填项');
            } else {
                message.error('操作失败: ' + error.message);
            }
        }
    };

    const openGrants = (record) => {
        setGrantsGroup(record);
        setNewSubscriptionId('');
        setGrants([]);
        setGrantsModalVisible(true);
        loadGrants(record.id);
    };

    const loadGrants = async (groupId) => {
        setGrantsLoading(true);
        try {
            const response = await cdnService.getSubscriptionGrants({ group_id: groupId });
            if (response.success) {
                setGrants(response.data || []);
            } else {
                message.error(response.error || '加载授权列表失败');
            }
        } catch (error) {
            message.error('加载授权列表失败: ' + error.message);
        } finally {
            setGrantsLoading(false);
        }
    };

    const handleAddGrant = async () => {
        const subId = String(newSubscriptionId).trim();
        if (!subId) {
            message.warning('请输入订阅ID');
            return;
        }
        setGrantSubmitting(true);
        try {
            const response = await cdnService.createSubscriptionGrant({
                subscription_id: subId,
                group_id: grantsGroup.id
            });
            if (response.success) {
                message.success('授权成功');
                setNewSubscriptionId('');
                loadGrants(grantsGroup.id);
            } else {
                message.error(response.error || '授权失败');
            }
        } catch (error) {
            message.error('授权失败: ' + error.message);
        } finally {
            setGrantSubmitting(false);
        }
    };

    const handleDeleteGrant = (grant) => {
        Modal.confirm({
            title: '取消授权',
            content: `确定要取消订阅 #${grant.subscription_id} 对该分组的授权吗？`,
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await cdnService.deleteSubscriptionGrant({ id: grant.id });
                    if (response.success) {
                        message.success('已取消授权');
                        loadGrants(grantsGroup.id);
                    } else {
                        message.error(response.error || '取消授权失败');
                    }
                } catch (error) {
                    message.error('取消授权失败: ' + error.message);
                }
            }
        });
    };

    // 根据表单选中的 zone 计算真实接入端点：record_prefix + '.' + zone
    const selectedZone = availableZones.find(z => z.id === selectedZoneId);
    const derivedEndpoint = editingGroup && editingGroup.record_prefix && selectedZone
        ? `${editingGroup.record_prefix}.${selectedZone.zone}`
        : null;

    const grantColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
        { title: '订阅ID', dataIndex: 'subscription_id', key: 'subscription_id', width: 100 },
        { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 90 },
        {
            title: '产品类型',
            dataIndex: 'product_type_code',
            key: 'product_type_code',
            render: (t) => (t ? <Tag color="geekblue">{t}</Tag> : '-')
        },
        {
            title: '订阅状态',
            dataIndex: 'sub_status',
            key: 'sub_status',
            render: (s) => (s ? <Tag color={s === 'active' ? 'green' : 'orange'}>{s}</Tag> : '-')
        },
        {
            title: '操作',
            key: 'action',
            width: 90,
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteGrant(record)}
                >
                    取消
                </Button>
            )
        }
    ];

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '分组名称',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    {record.user_id === 1 ? (
                        <GlobalOutlined style={{ color: '#1890ff' }} />
                    ) : (
                        <ApartmentOutlined />
                    )}
                    <strong>{text}</strong>
                    {record.user_id === 1 && (
                        <Tag color="blue">全局</Tag>
                    )}
                </Space>
            )
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'DNS Zone',
            dataIndex: 'dns_zone',
            key: 'dns_zone',
            render: (text) => (
                <Tag color="green">{text}</Tag>
            )
        },
        {
            title: 'CNAME接入域名',
            dataIndex: 'record_prefix',
            key: 'record_prefix',
            render: (prefix, record) => {
                const fullDomain = prefix && record.dns_zone ? `${prefix}.${record.dns_zone}` : '-';
                return (
                    <Space direction="vertical" size={0}>
                        <Tooltip title="点击复制">
                            <code
                                style={{
                                    background: '#f0f0f0',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigator.clipboard.writeText(fullDomain);
                                    message.success('已复制到剪贴板');
                                }}
                            >
                                {fullDomain} <CopyOutlined style={{ fontSize: 12, color: '#999' }} />
                            </code>
                        </Tooltip>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            前缀: {prefix || '-'}
                        </Text>
                    </Space>
                );
            }
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            key: 'priority',
            width: 80,
            sorter: (a, b) => a.priority - b.priority,
        },
        {
            title: <Tooltip title="已分配域名 / 上限（所有套餐合计，空=不限）">域名</Tooltip>,
            key: 'usage',
            width: 120,
            render: (_, r) => {
                const used = r.domains_used || 0;
                const cap = r.domain_cap;
                if (!cap) return <Text>{used} / ∞</Text>;
                if (used > cap) return <Tag color="red">{used} / {cap} 超 +{used - cap}</Tag>;
                return <Tag color={used >= cap * 0.9 ? 'orange' : 'default'}>{used} / {cap}</Tag>;
            }
        },
        {
            title: '线路数',
            dataIndex: 'line_count',
            key: 'line_count',
            width: 80,
            render: (count) => (
                <Badge count={count} showZero color="#52c41a" />
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? '启用' : '禁用'}
                </Tag>
            )
        },
        {
            title: '操作',
            key: 'action',
            width: 260,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="编辑">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        >
                            编辑
                        </Button>
                    </Tooltip>
                    <Tooltip title="按订阅单独授权该分组">
                        <Button
                            type="link"
                            icon={<SafetyCertificateOutlined />}
                            onClick={() => openGrants(record)}
                        >
                            授权管理
                        </Button>
                    </Tooltip>
                    <Tooltip title="删除">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.id, record.name)}
                            disabled={record.id === 1} // 禁止删除默认分组
                        >
                            删除
                        </Button>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card
                title={
                    <Space>
                        <ApartmentOutlined />
                        CDN分组管理
                    </Space>
                }
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadGroups}
                        >
                            刷新
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            新建分组
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={groups}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`
                    }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="暂无分组，点击右上角新建分组" /> }}
                />
            </Card>

            <Modal
                title={editingGroup ? '编辑CDN分组' : '新建CDN分组'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={600}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        priority: 100,
                        status: 'active'
                    }}
                >
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: t('Name') }, { key: 'description', label: t('Description'), type: 'textarea' }]}
                        base={<>
                    <Form.Item
                        name="name"
                        label="分组名称"
                        rules={[{ required: true, message: '请输入分组名称' }]}
                    >
                        <Input placeholder="例如：电信优化线路" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="描述"
                    >
                        <TextArea
                            rows={3}
                            placeholder="分组描述信息（可选）"
                        />
                    </Form.Item>
                        </>}
                    />

                    <Form.Item
                        name="dns_zone_id"
                        label="DNS Zone"
                        rules={[{ required: true, message: '请选择DNS Zone' }]}
                        extra="选择DNS Zone后，系统会自动生成CNAME接入域名（仅限全局Zone）"
                    >
                        <Select placeholder="选择DNS Zone">
                            {availableZones.map(zone => (
                                <Option key={zone.id} value={zone.id}>
                                    {zone.zone}
                                    {zone.description && ` - ${zone.description}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="CNAME接入域名（真实接入端点）">
                        <Input
                            readOnly
                            value={derivedEndpoint || ''}
                            addonAfter={derivedEndpoint ? (
                                <CopyOutlined
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        navigator.clipboard.writeText(derivedEndpoint);
                                        message.success('已复制到剪贴板');
                                    }}
                                />
                            ) : null}
                            placeholder={editingGroup ? '选择 DNS Zone 后自动生成' : '保存后由后端自动派生（record_prefix.zone）'}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            端点 = record_prefix.zone，由后端自动派生，无需手填。用户将域名 CNAME 到此端点接入。
                        </Text>
                    </Form.Item>

                    <Form.Item
                        name="priority"
                        label="优先级"
                        rules={[{ required: true, message: '请输入优先级' }]}
                        extra="数值越小优先级越高"
                    >
                        <InputNumber
                            min={1}
                            max={1000}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="domain_cap"
                        label="域名上限"
                        tooltip="所有套餐合计；空或 0 = 不限；只影响新域名"
                    >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="不限" />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="状态"
                        rules={[{ required: true, message: '请选择状态' }]}
                    >
                        <Select>
                            <Option value="active">启用</Option>
                            <Option value="inactive">禁用</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={
                    <Space>
                        <SafetyCertificateOutlined />
                        订阅授权管理{grantsGroup ? ` - ${grantsGroup.name}` : ''}
                    </Space>
                }
                open={grantsModalVisible}
                onCancel={() => setGrantsModalVisible(false)}
                width={760}
                footer={[
                    <Button key="close" onClick={() => setGrantsModalVisible(false)}>
                        关闭
                    </Button>
                ]}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="按订阅单独授权"
                    description="在此为指定订阅单独授权使用该 CDN 分组（per-订阅配置，替代过去的手写 SQL）。仅被授权的订阅可在用户侧看到并接入该分组。"
                />

                <Space style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="输入订阅ID"
                        value={newSubscriptionId}
                        onChange={(e) => setNewSubscriptionId(e.target.value)}
                        onPressEnter={handleAddGrant}
                        style={{ width: 220 }}
                        allowClear
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        loading={grantSubmitting}
                        onClick={handleAddGrant}
                    >
                        新增授权
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => grantsGroup && loadGrants(grantsGroup.id)}
                    >
                        刷新
                    </Button>
                </Space>

                <Table
                    columns={grantColumns}
                    dataSource={grants}
                    rowKey="id"
                    size="small"
                    loading={grantsLoading}
                    pagination={false}
                    locale={{ emptyText: '暂无单独授权的订阅' }}
                />
            </Modal>
        </div>
    );
};

export default CdnGroups;
